import React, { useState, useRef, useEffect } from 'react';
import YouTube, { YouTubeEvent, YouTubePlayer } from 'react-youtube';

interface PlayerProps {
  videoId: string;
  width?: string | number;
  height?: string | number;
}

interface PlayerState {
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  isFullscreen: boolean;
}

interface HTMLIFrameElementWithWebkit extends HTMLIFrameElement {
  webkitEnterFullscreen?: () => void;
  webkitRequestFullscreen?: () => void;
}

const Player: React.FC<PlayerProps> = ({ videoId, width = '100%', height = '100%' }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playerState, setPlayerState] = useState<PlayerState>({
    currentTime: 0,
    duration: 0,
    volume: 100,
    isMuted: false,
    isFullscreen: false,
  });
  const [isControlsVisible, setIsControlsVisible] = useState(true);
  const [showThumbnail, setShowThumbnail] = useState(true);
  const playerRef = useRef<YouTubePlayer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout>();
  const timeUpdateIntervalRef = useRef<NodeJS.Timeout>();

  // Add iOS detection
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const opts = {
    height: '100%',
    width: '100%',
    playerVars: {
      autoplay: 0,
      controls: isIOS ? 1 : 0,
      modestbranding: 1,
      rel: 0,
      showinfo: 0,
      iv_load_policy: 3,
      fs: 1,
      playsinline: 0,
      disablekb: 1,
      origin: window.location.origin,
      enablejsapi: 1,
      cc_load_policy: 0,
      annotations: 0,
      autohide: 1,
      widget_referrer: window.location.origin,
      playlist: videoId,
      loop: 1,
    },
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setPlayerState(prev => ({
        ...prev,
        isFullscreen: !!document.fullscreenElement,
      }));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
      }
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleOrientationChange = () => {
      const isLandscape = window.orientation === 90 || window.orientation === -90;
      if (isIOS && isLandscape) {
        // Force video to take full screen in landscape
        setPlayerState(prev => ({
          ...prev,
          isFullscreen: true,
        }));
      }
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
  }, [isIOS]);

  const startTimeUpdate = () => {
    if (timeUpdateIntervalRef.current) {
      clearInterval(timeUpdateIntervalRef.current);
    }

    timeUpdateIntervalRef.current = setInterval(() => {
      if (playerRef.current) {
        try {
          const currentTime = playerRef.current.getCurrentTime();
          const duration = playerRef.current.getDuration();
          if (!isNaN(currentTime) && !isNaN(duration)) {
            setPlayerState(prev => ({ ...prev, currentTime, duration }));
          }
        } catch (error) {
          console.error('Error updating time:', error);
        }
      }
    }, 1000);
  };

  const handleMouseMove = () => {
    setIsControlsVisible(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setIsControlsVisible(false);
      }, 3000);
    }
  };

  const togglePlay = () => {
    if (!playerRef.current) return;

    try {
      if (isPlaying) {
        playerRef.current.pauseVideo();
      } else {
        playerRef.current.playVideo();
      }
      setIsPlaying(!isPlaying);
    } catch (error) {
      console.error('Error toggling play state:', error);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (playerRef.current && !isNaN(time)) {
      try {
        playerRef.current.seekTo(time, true);
        setPlayerState(prev => ({ ...prev, currentTime: time }));
      } catch (error) {
        console.error('Error seeking:', error);
      }
    }
  };

  const handleVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    const volume = parseInt(e.target.value);
    if (playerRef.current && !isNaN(volume)) {
      try {
        playerRef.current.setVolume(volume);
        setPlayerState(prev => ({ ...prev, volume, isMuted: volume === 0 }));
      } catch (error) {
        console.error('Error setting volume:', error);
      }
    }
  };

  const toggleMute = () => {
    if (!playerRef.current) return;

    try {
      const newMuted = !playerState.isMuted;
      playerRef.current.setVolume(newMuted ? 0 : playerState.volume);
      setPlayerState(prev => ({ ...prev, isMuted: newMuted }));
    } catch (error) {
      console.error('Error toggling mute:', error);
    }
  };

  const toggleFullscreen = () => {
    if (isIOS) {
      try {
        // Use YouTube Player API's playVideo method which automatically
        // handles fullscreen on iOS when called from a user interaction
        if (playerRef.current) {
          // First ensure video is playing
          playerRef.current.playVideo();
          // Then call YouTube's native fullscreen method
          // @ts-ignore - YouTube API method
          playerRef.current.getIframe().contentWindow.postMessage(
            JSON.stringify({
              event: 'command',
              func: 'playVideo',
              args: [''],
              module: 'YT',
            }),
            '*'
          );
        }
      } catch (error) {
        console.error('Error handling iOS fullscreen:', error);
      }
    } else {
      if (!document.fullscreenElement) {
        containerRef.current?.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    }
  };

  const handleIOSFullscreen = () => {
    if (playerRef.current) {
      try {
        // Get the iframe element
        const iframe = playerRef.current.getIframe();
        // Create a fake click event at the YouTube fullscreen button position
        const event = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
        });
        // Dispatch the event on the iframe
        iframe.dispatchEvent(event);
      } catch (error) {
        console.error('Error triggering iOS fullscreen:', error);
      }
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const _onReady = (event: YouTubeEvent) => {
    playerRef.current = event.target;
    try {
      event.target.pauseVideo();
      startTimeUpdate();
    } catch (error) {
      console.error('Error in onReady:', error);
    }
  };

  const _onStateChange = (event: YouTubeEvent) => {
    try {
      // YouTube Player States: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (video cued)
      const playerState = event.data;

      if (playerState === 1) {
        // Playing
        setIsPlaying(true);
        // Hide thumbnail after 1 second of playing
        setTimeout(() => {
          setShowThumbnail(false);
        }, 5);
        startTimeUpdate();
      } else if (playerState === 2) {
        // Paused
        setIsPlaying(false);
        // Show thumbnail immediately when paused
        setShowThumbnail(true);
        if (timeUpdateIntervalRef.current) {
          clearInterval(timeUpdateIntervalRef.current);
        }
      }
    } catch (error) {
      console.error('Error in onStateChange:', error);
    }
  };

  const _onError = (error: any) => {
    console.error('YouTube Player Error:', error);
    setIsPlaying(false);
  };

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    background: '#000',
  };

  const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    background: 'transparent',
    zIndex: 2,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const youtubeWrapperStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    overflow: 'hidden',
  };

  const thumbnailStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    opacity: showThumbnail ? 1 : 0,
    transition: 'opacity 0.3s ease',
    zIndex: 1,
    pointerEvents: 'none',
  };

  return (
    <div
      ref={containerRef}
      style={containerStyle}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setIsControlsVisible(false)}
    >
      {/* Thumbnail Image */}
      <img
        src={`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`}
        style={thumbnailStyle}
        onError={e => {
          // Fallback to medium quality if maxres is not available
          (e.target as HTMLImageElement).src = `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;
        }}
      />

      <div style={overlayStyle} onClick={togglePlay}>
        {!isPlaying && (
          <div
            style={{
              width: '68px',
              height: '48px',
              backgroundColor: 'rgba(0,0,0,0.8)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 3,
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        )}
      </div>
      <div style={youtubeWrapperStyle} className="youtube-container">
        <YouTube
          videoId={videoId}
          opts={opts}
          onReady={_onReady}
          onStateChange={_onStateChange}
          onError={_onError}
          style={{
            width: '100%',
            height: '100%',
            opacity: isPlaying ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
          iframeClassName="youtube-iframe"
        />
      </div>

      {/* Custom Controls */}
      <div className={`custom-controls ${isControlsVisible ? 'visible' : ''}`}>
        <div className="progress-bar">
          <input
            type="range"
            min={0}
            max={playerState.duration}
            value={playerState.currentTime}
            onChange={handleSeek}
          />
          <div className="time-display">
            {formatTime(playerState.currentTime)} / {formatTime(playerState.duration)}
          </div>
        </div>

        <div className="controls-buttons">
          <button onClick={togglePlay}>
            {isPlaying ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          {/* Only show volume controls if not on iOS */}
          {!isIOS && (
            <div className="volume-control">
              <button onClick={toggleMute}>
                {playerState.isMuted ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                  </svg>
                )}
              </button>
              <input
                type="range"
                min={0}
                max={100}
                value={playerState.isMuted ? 0 : playerState.volume}
                onChange={handleVolume}
                className="volume-slider"
              />
            </div>
          )}

          <button onClick={isIOS ? handleIOSFullscreen : toggleFullscreen}>
            {playerState.isFullscreen ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
                <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
                <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <style>{`
        .youtube-container {
          position: relative;
          padding-bottom: 50.25%; /* 16:9 Aspect Ratio */
          height: 0;
          overflow: hidden;
        }

        .youtube-iframe {
          width: 100% !important;
          height: 100% !important;
          border: none !important;
          position: absolute;
          top: -60px;
          left: 0;
          width: calc(100% + 0px) !important;
          height: calc(100% + 120px) !important;
          border: none !important;
          background: black;
        }

        /* Hide iframe border */
        iframe {
          border: 0 !important;
        }

        /* Custom styles to hide YouTube elements */
        iframe[src*="youtube"] {
          margin-top: -0px !important;
        }

        .ytp-chrome-top,
        .ytp-chrome-bottom,
        .ytp-gradient-top,
        .ytp-gradient-bottom,
        .ytp-pause-overlay,
        .ytp-share-button,
        .ytp-watch-later-button,
        .ytp-watermark,
        .ytp-embed,
        .ytp-title,
        .ytp-endscreen-content,
        .ytp-ce-element {
          visibility: hidden !important;
          opacity: 0 !important;
          display: none !important;
        }

        .custom-controls {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(0,0,0,0.7));
          padding: 20px;
          z-index: 3;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .custom-controls.visible {
          opacity: 1;
        }

        .progress-bar {
          width: 100%;
          margin-bottom: 10px;
          position: relative;
        }

        .progress-bar input[type="range"] {
          width: 100%;
          height: 4px;
          background: rgba(255,255,255,0.3);
          border-radius: 2px;
          outline: none;
          -webkit-appearance: none;
          cursor: pointer;
          position: relative;
          z-index: 2;
          margin: 0;
          padding: 0;
          display: block;
        }

        .progress-bar input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 12px;
          height: 12px;
          background: #fff;
          border-radius: 50%;
          cursor: pointer;
          position: relative;
          z-index: 3;
          box-shadow: 0 1px 3px rgba(0,0,0,0.4);
          margin-top: -4px;
        }

        .progress-bar input[type="range"]::-webkit-slider-runnable-track {
          background: linear-gradient(to right, #ff0000 ${
            (playerState.currentTime / playerState.duration) * 100
          }%, rgba(255,255,255,0.3) ${(playerState.currentTime / playerState.duration) * 100}%);
          height: 4px;
          border-radius: 2px;
          border: none;
          outline: none;
        }

        .progress-bar input[type="range"]:hover::-webkit-slider-thumb {
          transform: scale(1.2);
          transition: transform 0.2s ease;
        }

        .time-display {
          color: #fff;
          font-size: 12px;
          margin-top: 8px;
          text-shadow: 0 1px 2px rgba(0,0,0,0.4);
          display: block;
        }

        .controls-buttons {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .controls-buttons button {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          color: #fff;
        }

        .volume-control {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .volume-slider {
          width: 80px;
          height: 4px;
          background: rgba(255,255,255,0.3);
          border-radius: 2px;
          outline: none;
          -webkit-appearance: none;
        }

        .volume-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 12px;
          height: 12px;
          background: #fff;
          border-radius: 50%;
          cursor: pointer;
        }

        /* Add iOS-specific styles */
        ${
          isIOS
            ? `
          .youtube-container {
            position: relative;
            padding-bottom: 56.25%; /* 16:9 aspect ratio */
            height: 0;
            overflow: hidden;
            background: black;
          }

          .youtube-iframe {
            width: 100% !important;
          height: 100% !important;
          border: none !important;
          position: absolute;
          top: -60px;
          left: 0;
          width: calc(100% + 0px) !important;
          height: calc(100% + 120px) !important;
          border: none !important;
          background: black;
          }

          /* Force hardware acceleration */
          .youtube-container, .youtube-iframe {
            -webkit-transform: translateZ(0);
            transform: translateZ(0);
          }
        `
            : ''
        }
      `}</style>
    </div>
  );
};

export default Player;
